import { useEffect } from "react";
import { useLocation } from "react-router";

const BASE_URL = "https://colourclubprinters.com";

const ROUTE_META: Record<string, { name: string; description: string }> = {
  "/": { name: "Home", description: "Colour Club Printers — premium packaging and printing solutions for global brands. FSC certified, ISO 9001, Sedex registered." },
  "/products": { name: "Products", description: "Explore our full range of premium packaging products including textile, garment, luxury, eco-friendly, and retail packaging." },
  "/products/textile-home-packaging": { name: "Textile & Home Packaging", description: "Custom textile and home packaging solutions designed for bedding, home fabrics, and lifestyle products." },
  "/products/garment-packaging": { name: "Garment Packaging", description: "Complete garment packaging solutions including labels, tags, and branding elements for fashion and apparel brands." },
  "/products/luxury-packaging": { name: "Luxury Packaging", description: "High-end luxury packaging crafted with premium materials and finishes for exclusive products." },
  "/products/labels-stickers": { name: "Labels & Stickers", description: "High-quality labels and stickers for product identification, retail, and logistics applications." },
  "/products/custom-packaging": { name: "Custom Packaging", description: "Fully customized packaging solutions tailored to your product, brand, and industry requirements." },
  "/products/retail-display-packaging": { name: "Retail & Display Packaging", description: "Retail and display packaging solutions designed to improve product visibility and in-store conversions." },
  "/products/eco-friendly-packaging": { name: "Eco-Friendly Packaging", description: "Sustainable packaging using eco-friendly materials and responsible production methods." },
  "/products/business-print-materials": { name: "Business Print Materials", description: "Professional business printing services including marketing materials and corporate stationery." },
  "/services": { name: "Services", description: "End-to-end packaging services from design and sampling to large-scale production and global delivery." },
  "/materials": { name: "Materials Library", description: "Explore our full range of packaging materials including SBS, kraft, duplex, corrugated, and chipboard." },
  "/finishes": { name: "Finishes", description: "Premium print finishing options including foil stamping, soft-touch lamination, embossing, spot UV, and more." },
  "/options": { name: "Packaging Options", description: "Browse all packaging options including box types, handles, coatings, and printing methods." },
  "/industries": { name: "Industries", description: "We serve fashion, retail, hospitality, cosmetics, and many more industries with tailored packaging solutions." },
  "/sustainability": { name: "Sustainability", description: "Our commitment to sustainable packaging through FSC certification, eco-friendly materials, and ethical sourcing." },
  "/process": { name: "Our Process", description: "From concept to delivery — discover how Colour Club Printers manages your packaging project end to end." },
  "/about": { name: "About Us", description: "Learn about Colour Club Printers — our heritage, team, certifications, and 30+ years of packaging excellence." },
  "/gallery": { name: "Gallery", description: "Browse our portfolio of premium packaging projects across fashion, retail, luxury, and lifestyle brands." },
  "/facility": { name: "Our Facility", description: "22,500 sq ft state-of-the-art production facility with dedicated manufacturing capabilities." },
  "/contact": { name: "Contact", description: "Get in touch with the Colour Club Printers team to discuss your packaging requirements." },
  "/quote": { name: "Get a Quote", description: "Request a custom packaging quote from Colour Club Printers. Fast turnaround, competitive pricing." },
  "/blog": { name: "Blog", description: "Expert insights, industry trends, and practical packaging guides from the Colour Club team." },
  "/privacy-policy": { name: "Privacy Policy", description: "Colour Club Printers privacy policy — how we collect, use, and protect your data." },
  "/terms": { name: "Terms & Conditions", description: "Terms and conditions for using Colour Club Printers services and website." },
  "/cookie-policy": { name: "Cookie Policy", description: "Information about how Colour Club Printers uses cookies on its website." },
};

function getBreadcrumbs(pathname: string): { name: string; path: string }[] {
  const crumbs: { name: string; path: string }[] = [{ name: "Home", path: "/" }];
  const segments = pathname.split("/").filter(Boolean);
  let current = "";
  for (const seg of segments) {
    current += "/" + seg;
    const meta = ROUTE_META[current];
    crumbs.push({ name: meta?.name ?? seg.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()), path: current });
  }
  return crumbs;
}

function buildOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Colour Club Printers",
    alternateName: ["Colour Club", "CCP Faisalabad", "Colour Club Printing Press"],
    url: BASE_URL,
    logo: `${BASE_URL}/logo.png`,
    image: `${BASE_URL}/socialicon.jpeg`,
    foundingDate: "1996",
    description: "Colour Club Printers is the No 1 printing press in Faisalabad, Pakistan, and a leading provider of premium custom packaging and garment label printing. Established in 1996, we specialize in high-quality woven labels, hang tags, custom boxes, luxury packaging, and eco-friendly solutions for global fashion brands. FSC certified, ISO 9001:2015, and Sedex registered, we provide international quality offset and digital printing services from our state-of-the-art 22,500 sq ft facility.",
    telephone: "+92-41-8750239",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Madina Market, Sugar Mor, Near PSO Pump, Sheikhupura Road, Nishatabad",
      addressLocality: "Faisalabad",
      addressRegion: "Punjab",
      addressCountry: "PK",
      postalCode: "38000",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: "31.4504",
      longitude: "73.1350",
    },
    areaServed: [
      { "@type": "Country", name: "Pakistan" },
      { "@type": "Country", name: "United Kingdom" },
      { "@type": "Country", name: "United States" },
      { "@type": "Country", name: "Germany" },
      { "@type": "Country", name: "France" },
      { "@type": "Country", name: "Italy" },
      { "@type": "Country", name: "Spain" },
      { "@type": "Country", name: "Australia" },
      { "@type": "Country", name: "Canada" },
      { "@type": "AdministrativeArea", name: "Faisalabad" },
      { "@type": "AdministrativeArea", name: "Lahore" },
      { "@type": "AdministrativeArea", name: "Karachi" },
    ],
    serviceType: [
      "Custom Packaging Manufacturing",
      "Offset Printing",
      "Digital Printing",
      "Luxury Packaging",
      "Garment Packaging",
      "Textile Packaging",
      "Eco-Friendly Packaging",
      "Labels and Stickers Printing",
      "Hang Tags Manufacturing",
      "Tissue Paper Printing",
      "Corrugated Box Manufacturing",
      "Retail Display Packaging",
      "Foil Stamping",
      "Embossing and Debossing",
      "Soft Touch Lamination",
      "Spot UV Printing",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+92-41-8750239",
      contactType: "customer service",
      availableLanguage: ["English", "Urdu"],
    },
    sameAs: [
      "http://facebook.com/colourclubprinters",
      "https://www.instagram.com/colourclubprinters/",
      "https://www.linkedin.com/company/colour-club-printers"
    ],
    hasCredential: [
      { "@type": "EducationalOccupationalCredential", name: "FSC Chain-of-Custody Certification" },
      { "@type": "EducationalOccupationalCredential", name: "ISO 9001:2015" },
      { "@type": "EducationalOccupationalCredential", name: "Sedex Member" },
    ],
  };
}

function buildFAQSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is the best printing press in Faisalabad?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Colour Club Printers is widely regarded as the best printing press in Faisalabad, Pakistan. With over 30 years of experience, FSC certification, and ISO 9001:2015 compliance, we offer premium custom packaging solutions for global brands.",
        },
      },
      {
        "@type": "Question",
        name: "Does Colour Club Printers offer custom packaging in Pakistan?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Colour Club Printers is a leading custom packaging manufacturer in Pakistan, offering custom boxes, hang tags, labels, tissue paper, garment packaging, luxury packaging, and eco-friendly packaging from our 22,500 sq ft facility in Faisalabad.",
        },
      },
      {
        "@type": "Question",
        name: "Do you manufacture garment packaging and hang tags?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, we specialise in garment packaging including woven labels, printed hang tags, polybags, tissue paper, and retail boxes for fashion and apparel brands worldwide.",
        },
      },
      {
        "@type": "Question",
        name: "Is Colour Club Printers FSC certified?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Colour Club Printers holds FSC Chain-of-Custody Certification, ISO 9001:2015 certification, and is a registered Sedex member, ensuring ethical and environmentally responsible manufacturing.",
        },
      },
      {
        "@type": "Question",
        name: "Do you ship packaging internationally?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, Colour Club Printers ships custom packaging globally including to the UK, USA, Europe, Australia, and the Middle East. We handle all export logistics and documentation.",
        },
      },
      {
        "@type": "Question",
        name: "What printing techniques do you offer?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We offer offset printing, digital printing, UV printing, foil stamping, embossing, debossing, soft-touch lamination, spot UV, matte and gloss coatings for all custom packaging products.",
        },
      },
    ],
  };
}

const ROUTE_FAQ: Record<string, { name: string; text: string }[]> = {
  "/products/textile-home-packaging": [
    { name: "What types of textile packaging do you offer?", text: "We offer custom pillow boxes, towel wraps, curtain boxes, bedding set packaging, rug tubes, and table linen boxes. All packaging is fully customizable with your brand colors, logo, and product photography." },
    { name: "Can you make custom sizes for home textile packaging?", text: "Yes, we manufacture textile packaging in any custom size to perfectly fit your bedding, towels, cushions, curtains, and other home textile products. We provide free structural design and sampling." },
    { name: "Do you use eco-friendly materials for textile packaging?", text: "Absolutely. We are FSC Chain-of-Custody certified and offer sustainable packaging options including recycled paperboard, soy-based inks, and biodegradable coatings for all textile packaging." },
  ],
  "/products/garment-packaging": [
    { name: "What is the minimum order for hang tags in Pakistan?", text: "Our minimum order for hang tags starts from 500 units. We offer competitive pricing for both small batches and large-volume orders, with consistent quality across all quantities." },
    { name: "Do you print woven labels and clothing tags?", text: "Yes, we specialize in woven labels, printed hang tags, care labels, size tags, brand labels, and all garment branding elements. We serve fashion brands worldwide from our facility in Faisalabad." },
    { name: "How long does garment packaging production take?", text: "Standard production takes 10-15 working days after sample approval. Rush orders can be completed in 7 days. We handle all export logistics and documentation for international shipping." },
  ],
  "/products/luxury-packaging": [
    { name: "What premium finishes are available for luxury boxes?", text: "We offer foil stamping (gold, silver, rose gold, custom colors), embossing, debossing, soft-touch lamination, spot UV, matte and gloss coatings, and magnetic closure mechanisms for luxury packaging." },
    { name: "Can you make rigid boxes for jewellery and cosmetics?", text: "Yes, we manufacture premium rigid boxes with custom foam inserts, satin lining, and magnetic closures for jewellery, cosmetics, perfumes, and luxury lifestyle products." },
    { name: "How much does luxury packaging cost in Pakistan?", text: "Luxury packaging pricing depends on size, material, finish type, and quantity. Contact us for a free custom quote — we offer competitive pricing with international quality standards from our Faisalabad facility." },
  ],
  "/products/labels-stickers": [
    { name: "What types of labels can you print?", text: "We print product labels, barcode labels, shipping labels, bottle labels, food labels, die-cut stickers, roll labels, vinyl stickers, transparent labels, and waterproof labels for all industries." },
    { name: "Do you offer waterproof and durable labels?", text: "Yes, we offer waterproof, oil-resistant, and UV-resistant labels using vinyl, BOPP, and polyester materials. These are ideal for products exposed to moisture, chemicals, or outdoor conditions." },
  ],
  "/products/custom-packaging": [
    { name: "How do I order custom packaging from Pakistan?", text: "Simply fill out our quote form with your product dimensions, quantity, and design preferences. Our team will respond within 24 hours with a competitive quote, and we provide free physical samples before mass production." },
    { name: "Can you design custom packaging for my brand?", text: "Yes, our in-house design team provides structural packaging design, graphic design, and 3D mockup services. We work closely with brand managers to create packaging that perfectly represents your brand identity." },
  ],
  "/products/eco-friendly-packaging": [
    { name: "What is FSC certified packaging?", text: "FSC (Forest Stewardship Council) certified packaging uses paper and board sourced from responsibly managed forests. Colour Club Printers holds FSC Chain-of-Custody certification, ensuring our materials are traceable and sustainably sourced." },
    { name: "Is your packaging recyclable?", text: "Yes, most of our packaging is fully recyclable. We offer recycled paperboard, water-based coatings, soy-based inks, and biodegradable lamination options to minimize environmental impact." },
  ],
  "/products/retail-display-packaging": [
    { name: "What types of retail display packaging do you offer?", text: "We manufacture counter displays, shelf-ready packaging (SRP), PDQ trays, floor standing displays, dump bins, and custom point-of-sale displays designed to maximize product visibility and drive in-store conversions." },
  ],
  "/products/business-print-materials": [
    { name: "What business print materials do you offer?", text: "We print business cards, brochures, catalogues, letterheads, envelopes, company profiles, flyers, posters, and all corporate stationery using premium offset and digital printing techniques." },
    { name: "Where can I get premium business cards printed in Faisalabad?", text: "Colour Club Printers offers luxury business card printing in Faisalabad with options including foil stamping, embossing, soft-touch coating, and premium card stocks up to 600gsm." },
  ],
  "/services": [
    { name: "How long does custom packaging production take?", text: "Standard production takes 10-15 working days after sample approval. Complex luxury packaging may take 15-20 days. We offer rush production for urgent orders and handle all export logistics for international delivery." },
    { name: "Do you offer packaging design services?", text: "Yes, we provide end-to-end packaging design services including structural design, graphic design, 3D mockups, and physical sampling. Our design team works with your brand guidelines to create packaging that elevates your products." },
  ],
  "/sustainability": [
    { name: "What sustainability certifications does Colour Club Printers hold?", text: "We hold FSC Chain-of-Custody Certification for responsibly sourced materials, ISO 9001:2015 for quality management, and are a registered Sedex member ensuring ethical and socially responsible manufacturing practices." },
    { name: "How do you reduce environmental impact in packaging production?", text: "We use FSC-certified paper, soy-based inks, water-based coatings, energy-efficient machinery, and waste recycling programs. We also offer biodegradable and compostable packaging alternatives to help brands reduce their environmental footprint." },
  ],
};

function buildRouteFAQSchema(pathname: string) {
  const faqs = ROUTE_FAQ[pathname];
  if (!faqs || faqs.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.name,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.text,
      },
    })),
  };
}

const PRODUCT_SCHEMA_DATA: Record<string, { name: string; description: string; image: string; category: string }> = {
  "/products/textile-home-packaging": {
    name: "Custom Textile & Home Packaging",
    description: "Premium custom textile and home packaging solutions including pillow boxes, towel wraps, bedding set packaging, and curtain boxes. FSC-certified, fully customizable.",
    image: "/products/textile-home-packaging.png",
    category: "Textile & Home Packaging",
  },
  "/products/garment-packaging": {
    name: "Garment Packaging & Labels",
    description: "Complete garment packaging including woven labels, hang tags, shirt boxes, garment bags, and retail boxes for fashion and apparel brands worldwide.",
    image: "/products/garment-packaging.png",
    category: "Garment Packaging",
  },
  "/products/luxury-packaging": {
    name: "Luxury Packaging & Rigid Boxes",
    description: "High-end luxury packaging with foil stamping, embossing, soft-touch lamination, magnetic closures, and premium rigid box construction for luxury brands.",
    image: "/products/luxury-packaging.png",
    category: "Luxury Packaging",
  },
  "/products/labels-stickers": {
    name: "Custom Labels & Stickers",
    description: "High-quality custom labels and stickers including die-cut, roll labels, waterproof, barcode, and vinyl stickers for all industries and applications.",
    image: "/products/labels-stickers.png",
    category: "Labels & Stickers",
  },
  "/products/custom-packaging": {
    name: "Fully Custom Packaging Solutions",
    description: "Bespoke custom packaging solutions in any shape, size, and material. Structural design, sampling, and full-scale production from one manufacturer.",
    image: "/products/custom-packaging.png",
    category: "Custom Packaging",
  },
  "/products/eco-friendly-packaging": {
    name: "Eco-Friendly & Sustainable Packaging",
    description: "FSC-certified sustainable packaging using recycled materials, soy-based inks, and biodegradable options. Eco-friendly without compromising quality.",
    image: "/products/eco-friendly-packaging.png",
    category: "Eco-Friendly Packaging",
  },
  "/products/retail-display-packaging": {
    name: "Retail & Display Packaging",
    description: "Counter displays, shelf-ready packaging, PDQ trays, and point-of-sale displays designed to maximize product visibility and drive in-store conversions.",
    image: "/products/retail-display-packaging.png",
    category: "Retail Display Packaging",
  },
  "/products/business-print-materials": {
    name: "Business Print Materials",
    description: "Professional business cards, brochures, catalogues, letterheads, and corporate stationery with premium offset and digital printing.",
    image: "/products/business-print-materials.png",
    category: "Business Print Materials",
  },
};

function buildProductSchema(pathname: string) {
  const product = PRODUCT_SCHEMA_DATA[pathname];
  if (!product) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.description,
    image: `${BASE_URL}${product.image}`,
    category: product.category,
    brand: {
      "@type": "Brand",
      name: "Colour Club Printers",
    },
    manufacturer: {
      "@type": "Organization",
      name: "Colour Club Printers",
      url: BASE_URL,
    },
    offers: {
      "@type": "Offer",
      availability: "https://schema.org/InStock",
      priceCurrency: "USD",
      price: "0",
      priceValidUntil: "2027-12-31",
      url: `${BASE_URL}${pathname}`,
      seller: {
        "@type": "Organization",
        name: "Colour Club Printers",
      },
    },
  };
}

function buildWebsiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Colour Club Printers",
    url: BASE_URL,
    potentialAction: {
      "@type": "SearchAction",
      target: `${BASE_URL}/products?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

function buildWebPageSchema(pathname: string) {
  const meta = ROUTE_META[pathname];
  if (!pathname || pathname === "/") return null;
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: meta?.name ?? "Colour Club Printers",
    description: meta?.description ?? "",
    url: `${BASE_URL}${pathname}`,
    isPartOf: { "@type": "WebSite", url: BASE_URL },
    breadcrumb: buildBreadcrumbSchema(pathname),
  };
}

function buildBreadcrumbSchema(pathname: string) {
  const crumbs = getBreadcrumbs(pathname);
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((crumb, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: crumb.name,
      item: `${BASE_URL}${crumb.path}`,
    })),
  };
}

function buildLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Colour Club Printers",
    image: `${BASE_URL}/logo.png`,
    url: BASE_URL,
    telephone: "+92-41-8750239",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Madina Market, Sugar Mor, Near PSO Pump, Sheikhupura Road, Nishatabad",
      addressLocality: "Faisalabad",
      addressRegion: "Punjab",
      postalCode: "38000",
      addressCountry: "PK",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: "31.4504",
      longitude: "73.1350",
    },
    priceRange: "$$",
    sameAs: [
      "http://facebook.com/colourclubprinters",
      "https://www.instagram.com/colourclubprinters/",
      "https://www.linkedin.com/company/colour-club-printers"
    ],
    openingHoursSpecification: [
      { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"], opens: "09:00", closes: "18:00" },
    ],
  };
}

function injectSchema(id: string, data: object | null) {
  if (!data) return;
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeSchema(id: string) {
  document.getElementById(id)?.remove();
}

export function SchemaMarkup() {
  const { pathname } = useLocation();

  useEffect(() => {
    injectSchema("schema-org", buildOrganizationSchema());
    injectSchema("schema-website", buildWebsiteSchema());
    injectSchema("schema-localbusiness", buildLocalBusinessSchema());

    // Homepage gets the global FAQ; product/service pages get per-route FAQs
    if (pathname === "/") {
      injectSchema("schema-faq", buildFAQSchema());
    } else {
      const routeFaq = buildRouteFAQSchema(pathname);
      if (routeFaq) {
        injectSchema("schema-faq", routeFaq);
      } else {
        removeSchema("schema-faq");
      }
    }

    // Product schema for product category pages
    const productSchema = buildProductSchema(pathname);
    if (productSchema) {
      injectSchema("schema-product", productSchema);
    } else {
      removeSchema("schema-product");
    }

    const webPage = buildWebPageSchema(pathname);
    if (webPage) {
      injectSchema("schema-webpage", webPage);
      injectSchema("schema-breadcrumb", buildBreadcrumbSchema(pathname));
    } else {
      removeSchema("schema-webpage");
      removeSchema("schema-breadcrumb");
    }
  }, [pathname]);

  return null;
}

export { getBreadcrumbs, ROUTE_META, BASE_URL };
